import { Modal, Spin } from "antd";
import { Suspense, useCallback, useEffect, useState } from "react";
import { lazyWithReload } from "@/lib/lazy-with-reload";

import { useTranslation } from "react-i18next";

const IfcAssemblyViewer = lazyWithReload(() => import("./IfcAssemblyViewer"));

export default function IfcViewerModal({
  partsUrl,
  open,
  onClose,
  assemblyMark,
}: {
  partsUrl: string | null;
  open: boolean;
  onClose: () => void;
  assemblyMark?: string | null | Array<string | null | undefined>;
}) {
  const { t } = useTranslation();
  const [localFile, setLocalFile] = useState<File | null>(null);

  useEffect(() => {
    if (!open) setLocalFile(null);
  }, [open]);

  const sourceName =
    localFile?.name || t("ifcViewer.title") || "3D 圖面預覽";
  const markLabel = (
    Array.isArray(assemblyMark) ? assemblyMark : [assemblyMark]
  )
    .find((mark) => Boolean(mark?.trim()))
    ?.trim();
  const title = markLabel ? `${markLabel}｜${sourceName}` : sourceName;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={title}
      width="92%"
      style={{ maxWidth: 1100 }}
      centered
      zIndex={2000}
      destroyOnHidden
      afterOpenChange={(visible) => {
        if (visible) window.dispatchEvent(new Event("resize"));
      }}
      styles={{
        content: {
          display: "flex",
          flexDirection: "column",
          maxHeight: "90vh",
          overflow: "hidden",
        },
        body: { padding: 0, flex: 1, minHeight: 0, overflow: "hidden" },
      }}
      footer={null}
      // footer={[
      //   <Upload
      //     key="pick"
      //     accept=".ifc"
      //     showUploadList={false}
      //     beforeUpload={(picked) => {
      //       setLocalFile(picked as unknown as File);
      //       return false;
      //     }}
      //   >
      //     <Button>{t("ifcViewer.pickFile") || "選擇本機 .ifc 檔"}</Button>
      //   </Upload>,
      //   <Button key="close" type="primary" onClick={onClose}>
      //     {t("ifcViewer.close") || "關閉"}
      //   </Button>,
      // ]}
    >
      <div
        style={{ height: "calc(90vh - 80px)", minHeight: 240 }}
        className="w-full"
      >
        {localFile || partsUrl ? (
          <Suspense
            fallback={
              <div className="flex h-full w-full items-center justify-center">
                <Spin />
              </div>
            }
          >
            <IfcAssemblyViewer
              key={`${
                localFile ? `local:${localFile.name}` : `url:${partsUrl}`
              }|${
                Array.isArray(assemblyMark)
                  ? assemblyMark.join("|")
                  : (assemblyMark ?? "")
              }`}
              url={localFile ? null : partsUrl}
              localFile={localFile}
              assemblyMark={assemblyMark}
            />
          </Suspense>
        ) : (
          <div className="flex h-full w-full items-center justify-center p-6 text-center text-sm text-gray-500">
            {t("ifcViewer.notFound") ||
              "這個專案還沒有 IFC 分析結果"}
          </div>
        )}
      </div>
    </Modal>
  );
}

export function useIfcPreview() {
  const [open, setOpen] = useState(false);
  const [partsUrl, setPartsUrl] = useState<string | null>(null);
  const [assemblyMark, setAssemblyMark] = useState<
    string | null | Array<string | null | undefined>
  >(null);

  const openIfc = useCallback(
    (
      next: string | null,
      mark?: string | null | Array<string | null | undefined>,
    ) => {
      setPartsUrl(next);
      setAssemblyMark(mark ?? null);
      setOpen(true);
    },
    [],
  );
  const closeIfc = useCallback(() => setOpen(false), []);

  return {
    ifcPartsUrl: partsUrl,
    assemblyMark,
    openIfc,
    closeIfc,
    ifcPreview: (
      <IfcViewerModal
        partsUrl={partsUrl}
        open={open}
        onClose={closeIfc}
        assemblyMark={assemblyMark}
      />
    ),
  };
}
